<template>
  <transition name="edit-fade" mode="out-in">
    <div class="columns themes-wrapper" v-if="!edit && !add" :key="'1'">
      <loader :loading="loading.list" :fixed="false"></loader>
      <div class="column is-9 themes">
        <div class="field filter">
          <p class="control has-icons-right">
            <input
              class="input"
              type="text"
              :placeholder="$resx('Global.Filter')"
              v-model="input"
            />
            <span class="icon is-small is-right" @click.prevent="input = ''">
              <font-awesome-icon :icon="icons.delete" />
            </span>
          </p>
        </div>
        <ul class="themes-list items-list">
          <li
            v-for="(theme, idx_l) in filtered"
            :key="idx_l"
            @click="selectTheme(idx_l)"
            :class="{ selected: selected === idx_l }"
          >
            <div class="theme">
              <font-awesome-icon
                :icon="getThemeIcon(theme)"
                @click.stop="$store.dispatch('setPreviewTheme', theme)"
              />
              {{ theme.Title }}
            </div>
            <div class="date">
              {{ formatDate(theme.PublicationDate) }}
            </div>
          </li>
        </ul>
      </div>
      <div class="column is-3">
        <div class="buttons">
          <button class="button is-fullwidth" @click.prevent="addTheme">
            <span class="icon">
              <font-awesome-icon :icon="icons.add" />
            </span>
            <span>{{ $resx("Buttons.Add") }}</span>
          </button>
          <button
            class="button is-fullwidth"
            :disabled="selected > -1 ? false : 'disabled'"
            @click.prevent="
              (edit = true),
                $emit('canClose', false),
                $store.commit('contentChanged', true)
            "
          >
            <span class="icon">
              <font-awesome-icon :icon="icons.edit" />
            </span>
            <span>{{ $resx("Buttons.Edit") }}</span>
          </button>
          <button
            class="button is-fullwidth is-danger"
            :disabled="selected > -1 ? false : 'disabled'"
            @click.prevent="deleteTheme"
          >
            <span class="icon">
              <font-awesome-icon :icon="icons.delete" />
            </span>
            <span>{{ $resx("Buttons.Delete") }}</span>
          </button>
        </div>
      </div>
    </div>
    <div class="columns form-wrapper" v-else :key="'2'">
      <loader :loading="loading.edit" :fixed="false"></loader>
      <form class="column is-9">
        <div class="tabs">
          <ul>
            <li :class="{ 'is-active': tabIndex === 1 }">
              <a @click.prevent="tabIndex = 1">
                {{ $resx("Theme.Tab1") }}
              </a>
            </li>
            <li :class="{ 'is-active': tabIndex === 2 }">
              <a @click.prevent="tabIndex = 2">
                {{ $resx("Theme.Tab2") }}
              </a>
            </li>
          </ul>
        </div>
        <transition name="tab-fade" mode="out-in">
          <div v-if="tabIndex === 1" :key="tabIndex">
            <file-input
              v-if="staticTheme.Image || staticTheme.Image === ''"
              :value.sync="staticTheme.Image"
              @complete="setImage"
              :profile="'theme'"
              :disabledProfiles="[
                'header',
                'meta',
                'inline',
                'fullwidth',
                'image',
                'icon',
                'avatar',
                'partner',
              ]"
            ></file-input>
            <div class="columns">
              <div class="field column">
                <label class="label">{{ $resx("Align.Title") }}</label>
                <div class="control">
                  <div class="field has-addons">
                    <p class="control" v-for="(item, idx) in align" :key="idx">
                      <button
                        class="button"
                        :class="{
                          'is-success': staticTheme.TitleAlign === item,
                        }"
                        @click.prevent="staticTheme.TitleAlign = item"
                      >
                        <span>{{ $resx("Align." + item) }}</span>
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="field">
              <label class="label">{{ $resx("Theme.Title") }}</label>
              <div class="control">
                <input class="input" type="text" v-model="staticTheme.Title" />
              </div>
            </div>
            <div class="field">
              <label class="label">{{ $resx("Theme.Subtitle") }}</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  v-model="staticTheme.Subtitle"
                />
              </div>
            </div>
            <div class="field">
              <label class="label">{{ $resx("Theme.Introduction") }}</label>
              <div class="control">
                <textarea
                  class="textarea"
                  v-bind:value="staticTheme.Introduction"
                  v-on:input="staticTheme.Introduction = $event.target.value"
                ></textarea>
              </div>
            </div>
          </div>
          <div v-else-if="tabIndex === 2" :key="tabIndex">
            <div class="field">
              <label class="label">{{ $resx("Theme.Markdown") }}</label>
              <div class="control">
                <markdown
                  :value="staticTheme.Markdown"
                  @update="updateMarkdown"
                  :autosave="false"
                  :wordCount="false"
                  :keepactive="true"
                  :notInToolbar="[
                    'save',
                    'close',
                    'toggleMenu',
                    'insertCustom',
                    'fullWidth'
                  ]"
                  @setImage="setUploadedImage"
                ></markdown>
              </div>
            </div>
          </div>
        </transition>
      </form>
      <div class="column is-3">
        <div class="buttons">
          <button
            class="button is-fullwidth is-success"
            @click.prevent="submitTheme('submit')"
            :disabled="submitEnabled ? false : 'disabled'"
          >
            <span>{{ $resx("Buttons.Save") }}</span>
          </button>
          <button
            class="button is-fullwidth"
            @click.prevent="submitTheme('cancel')"
          >
            <span>{{ $resx("Buttons.Cancel") }}</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import fileInput from '@/components/admin/FileInput.vue'
import {
  faPlus,
  faPencil,
  faTimes,
  faStar as faStarLight,
  faEye
} from '@fortawesome/pro-regular-svg-icons'
import { faStar, faEye as eyeSolid } from '@fortawesome/pro-solid-svg-icons'
import { getFilenameFromUrl } from '@/mixins/getFilenameFromUrl.js'
import { confirm } from '@/mixins/confirm.js'
import markdown from '@/components/admin/Markdown.vue'
import loader from '@/components/Loader.vue'

export default {
  name: 'modalContentThemes',
  data () {
    return {
      loading: {
        edit: false,
        list: false
      },
      publicationAddModal: false,
      publicationAddUrl: '',
      publicationModal: false,
      tabIndex: 1,
      selected: -1,
      selectedPublicationIndex: -1,
      input: '',
      align: ['Left', 'Center', 'Right'],
      oldImages: [],
      uploadedImages: [],
      colors: [
        {
          Hex: '#ff0000',
          Id: 'Red'
        },
        {
          Hex: '#ffffff',
          Id: 'White'
        },
        {
          Hex: '#000000',
          Id: 'Black'
        }
      ],
      drag: false,
      icons: {
        add: faPlus,
        edit: faPencil,
        delete: faTimes,
        star: faStar,
        starLight: faStarLight,
        preview: eyeSolid,
        previewLight: faEye
      },
      add: false,
      edit: false,
      staticPublication: false,
      staticTheme: false,
      availableCultures: ['nl-NL', 'en-GB'],
      selectedForDelete: [],
      templates: {
        Theme: {
          Image: '',
          ImageGrayscale: '',
          ImageOverlay: '',
          Title: '',
          TitleAlign: 'Left',
          TitleColor: '',
          Subtitle: '',
          Introduction: '',
          Markdown: '',
          Culture: ''
        }
      }
    }
  },
  mixins: [confirm, getFilenameFromUrl],
  components: {
    fileInput,
    markdown,
    loader
    // viaModal
  },
  computed: {
    allThemes () {
      return this.$store.state.themes
    },
    cultures () {
      var tmp = this.availableCultures.slice()
      if (this.staticTheme.Cultures) {
        var keys = Object.keys(this.staticTheme.Cultures)
        for (var i = 0; i < keys.length; i++) {
          var idx = tmp.indexOf(keys[i])
          if (idx > -1) {
            tmp.splice(idx, 1)
          }
        }
        return tmp
      } else {
        return tmp
      }
    },
    submitEnabled () {
      if (this.add) {
        return this.staticTheme.Image !== ''
      } else {
        return (
          JSON.stringify(this.staticTheme) !==
          JSON.stringify(this.filtered[this.selected])
        )
      }
    },
    selectedTheme () {
      if (this.selected > -1) {
        return this.filtered[this.selected]
      } else {
        return false
      }
    },
    selectedPublication () {
      if (this.staticTheme.Publications) {
        return this.staticTheme.Publications[this.selectedPublicationIndex]
      } else {
        return false
      }
    },
    filtered () {
      var filtered = []
      var self = this
      var input = self.input.toLowerCase()
      self.$store.state.themes.forEach((theme) => {
        if (
          theme.Title.toLowerCase().indexOf(input) > -1 &&
          theme.Culture === self.$store.state.language
        ) {
          filtered.push(theme)
        }
      })
      this.unselectTheme()
      filtered.sort(function (a, b) {
        if (
          a.PublicationDate === undefined ||
          a.PublicationDate === null ||
          a.PublicationDate === ''
        ) {
          return -1
        } else {
          return +new Date(b.PublicationDate) - +new Date(a.PublicationDate)
        }
      })
      return filtered
    },
    dragOptions () {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost'
      }
    }
  },
  mounted () {
    var self = this
    self.loading.list = true
    self.$store.dispatch('getThemes', false).then(function () {
      self.loading.list = false
    })
  },
  methods: {
    addPublication: function () {
      var self = this
      self.loading.edit = true
      console.log(self.publicationAddUrl)

      // check URL?
      // ThemeID not needed

      self
        .$http({
          method: 'POST',
          url: '/publication',
          api: 'website',
          data: {
            Url: self.publicationAddUrl
          }
        })
        .then(function (response) {
          console.log(response)
          // set publication index to the new index
          // launch publication edit modal
        })
        .catch(function (err) {
          self.$store.dispatch('setSnack', {
            message: err.message
          })
          self.loading.edit = false
        })
      self.publicationAddUrl = ''
    },
    selectPublication: function (idx) {
      this.selectedPublicationIndex = idx
      this.publicationModal = true
    },
    getThemeIcon: function (theme) {
      var self = this
      var lastTheme = self.$store.state.lastTheme.filter((theme) => {
        return theme.Culture === self.$store.state.language
      })
      if (
        (!self.$store.state.previewTheme ||
          self.$store.state.previewTheme.Culture !==
            self.$store.state.language) &&
        lastTheme.length > 0 &&
        lastTheme[0].ID === theme.ID
      ) {
        return self.icons.preview
      } else if (theme.ID === self.$store.state.previewTheme.ID) {
        return self.icons.preview
      } else {
        return self.icons.previewLight
      }
    },
    formatDate: function (string) {
      var tmp = ''
      if (string && string !== '') {
        var d = new Date(string)
        tmp =
          ('0' + d.getDate()).slice(-2) +
          '-' +
          ('0' + (d.getMonth() + 1)).slice(-2) +
          '-' +
          d.getFullYear() +
          ' ' +
          ('0' + d.getHours()).slice(-2) +
          ':' +
          ('0' + d.getMinutes()).slice(-2)
      }
      return tmp
    },
    updateMarkdown: function (value) {
      var self = this
      self.staticTheme.Markdown = value
    },
    unselectTheme: function () {
      this.selected = -1
      this.staticTheme = false
      this.oldImages = []
      this.uploadedImages = []
    },
    setImage: function (data) {
      this.staticTheme.Image = data.url
      this.staticTheme.ImageGrayscale = data.grayscale
      this.staticTheme.ImageOverlay = data.overlay
    },
    selectTheme: function (idx) {
      if (this.selected === idx) {
        this.unselectTheme()
      } else {
        this.tabIndex = 1
        this.selected = idx
        this.staticTheme = JSON.parse(
          JSON.stringify(this.filtered[this.selected])
        )
        this.oldImages = this.filterImages(
          this.filtered[this.selected].Markdown
        )
      }
    },
    addTheme: function () {
      this.staticTheme = JSON.parse(JSON.stringify(this.templates.Theme))
      this.staticTheme.Culture = this.$store.state.language
      this.add = true
      this.$emit('canClose', false)
      this.$store.commit('contentChanged', true)
    },
    filterImages: function (input) {
      // var rgx = /!\[.*?\]\((.*?)\)/g
      var rgx = /!\[.*?\]\((.*?)\)|url\((.*?)\)|img:'(.*?)'/g
      var match = rgx.exec(input)
      var tmp = []
      while (match != null) {
        if (match[1]) {
          tmp.push(match[1])
        } else if (match[2]) {
          tmp.push(match[2])
        } else if (match[3]) {
          tmp.push(match[3])
        }
        match = rgx.exec(input)
      }
      return tmp
    },
    removeUnneededImages: function (o, n) {
      var self = this
      for (var i = 0; i < o.length; i++) {
        if (!n.includes(o[i])) {
          var file = self.getFilenameFromUrl(o[i])
          self.$store.dispatch('deleteFile', file)
        }
      }
      for (i = 0; i < self.uploadedImages.length; i++) {
        if (!n.includes(self.uploadedImages[i])) {
          var file2 = self.getFilenameFromUrl(self.uploadedImages[i])
          self.$store.dispatch('deleteFile', file2)
        }
      }
      self.uploadedImages = []
    },
    setUploadedImage: function (url) {
      this.uploadedImages.push(url)
    },
    deleteImage: function (canceled) {
      var self = this
      var newObj
      var oldObj
      if (canceled) {
        oldObj = self.staticTheme
        newObj = self.filtered[self.selected] || {}
      } else {
        oldObj = self.filtered[self.selected]
        newObj = self.staticTheme
      }
      if (oldObj && newObj && self.submitEnabled) {
        if (oldObj.Image !== '' && newObj.Image !== oldObj.Image) {
          var file = self.getFilenameFromUrl(oldObj.Image)
          self.$store.dispatch('deleteFile', file)
        }
        if (
          oldObj.Image !== '' &&
          newObj.ImageGrayscale !== oldObj.ImageGrayscale
        ) {
          var file2 = self.getFilenameFromUrl(oldObj.ImageGrayscale)
          self.$store.dispatch('deleteFile', file2)
        }

        if (
          oldObj.Image !== '' &&
          newObj.ImageOverlay !== oldObj.ImageOverlay
        ) {
          var image = oldObj.ImageOverlay || newObj.ImageOverlay
          var file3 = self.getFilenameFromUrl(image)
          self.$store.dispatch('deleteFile', file3)
        }
      }
    },
    deleteTheme: function () {
      var self = this
      var message
      if (self.selected > -1) {
        message = self.$resx('Global.AreYouSure')
        self.confirm({
          message: message,
          buttons: [
            {
              name: self.$resx('Buttons.Cancel'),
              class: 'is-pulled-left'
            },
            {
              name: self.$resx('Buttons.Delete'),
              class: 'is-pulled-right is-danger',
              action: function () {
                self.loading.list = true
                var theme = self.filtered[self.selected]
                self
                  .$http({
                    method: 'DELETE',
                    url: '/themes/' + theme.ID,
                    name:
                      process.env.VUE_APP_NAME +
                      '_' +
                      self.$store.state.language,
                    api: 'website'
                  })
                  .then(function (response) {
                    self.$store.dispatch('getThemes', true).then(function () {
                      self.$store.dispatch('setSnack', {
                        message: self.$resx('Theme.Removed')
                      })
                      self.loading.list = false
                      self.selected = -1
                      if (
                        self.$store.state.previewTheme &&
                        self.$store.state.previewTheme.ID ===
                          self.filtered[0].ID
                      ) {
                        self.$store.commit('setPreviewTheme', false)
                      }
                    })
                  })
                  .catch(function (err) {
                    self.$store.dispatch('setSnack', {
                      message: err.message
                    })
                    self.loading.list = false
                  })
              }
            }
          ]
        })
      }
    },
    resetSelected: function () {
      var self = this
      self.staticTheme = false
      self.selected = -1
      self.uploadedImages = []
      self.add = false
      self.edit = false
      self.$emit('canClose', true)
      self.$store.commit('contentChanged', false)
    },
    submitTheme: function (action) {
      var self = this
      if (action === 'submit') {
        // save header
        self.loading.edit = true
        // var preview = JSON.parse(JSON.stringify(self.staticTheme))
        self
          .$http({
            method: self.add ? 'POST' : 'PUT',
            url: '/themes',
            api: 'website',
            data: self.staticTheme,
            name: process.env.VUE_APP_NAME + '_' + self.$store.state.language
          })
          .then(function (response) {
            self.deleteImage(false)

            var images = self.filterImages(self.staticTheme.Markdown)
            self.removeUnneededImages(self.oldImages, images)

            // var title = self.staticTheme.Title
            self.$store.dispatch('getThemes', true).then(function () {
              // self.loading.edit = false
              self.resetSelected()
              self.$store.dispatch('getSitemap', true).then(function () {
                // console.log('l', preview)
                // self.$store.dispatch('setPreviewTheme', response)

                self.loading.edit = false
                self.$store.dispatch('setSnack', {
                  message: self.$resx('Theme.Saved')
                })
              })
            })
          })
          .catch(function (err) {
            self.$store.dispatch('setSnack', {
              message: err.message
            })
            self.loading.edit = false
          })
      } else {
        self.deleteImage(true)
        for (var i = 0; i < self.uploadedImages.length; i++) {
          var file2 = self.getFilenameFromUrl(self.uploadedImages[i])
          self.$store.dispatch('deleteFile', file2)
        }
        self.uploadedImages = []
        self.resetSelected()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.edit-fade-enter-active,
.edit-fade-leave-active,
.tab-fade-enter-active,
.tab-fade-leave-active {
  transition: opacity 0.2s;
}
.edit-fade-enter,
.edit-fade-leave-to,
.tab-fade-enter,
.tab-fade-leave-to {
  opacity: 0;
}
.flip-list-move {
  transition: transform 0.5s;
}
.ghost {
  opacity: 0.3;
}

.themes-wrapper {
  height: 450px;

  .themes {
    .filter {
      display: inline-block;
      vertical-align: top;
      width: calc(100% - 0px);
      margin-right: 0px;
      margin-bottom: 0;

      .control {
        .icon {
          pointer-events: all;
          cursor: pointer !important;
        }
      }
    }

    .themes-list {
      li {
        .theme {
          display: inline-block;
          vertical-align: middle;
          width: calc(100% - 150px);
          @include text-overflow;

          svg {
            // vertical-align: top;
            margin-right: 10px;
            color: $grey-lighter;

            &:hover {
              color: $grey-light;
            }

            &[data-prefix="fas"] {
              color: $warning;
            }
          }
        }
        .date {
          display: inline-block;
          vertical-align: top;
          width: 150px;
          text-align: right;
          @include text-overflow;
        }
      }
    }
  }
}
.form-wrapper {
  > .is-9 {
    height: 100%;
    overflow: auto;

    /deep/.vue-simplemde {
      line-height: 26px;
      font-size: 16px;
      // .editor-toolbar{
      //   // border-radius: 4px;
      //   // border: 1px solid #bbb;
      // }
      .CodeMirror {
        height: 440px;
      }
      .CodeMirror,
      .CodeMirror-scroll {
        background-color: whitesmoke;
        // border: none;
      }
      .editor-preview {
        background-color: $white;
      }
    }
  }
}
</style>
